<script lang="ts">
  import { Link, inertia, page } from "@inertiajs/svelte"
  import { _, locale } from "svelte-i18n"

  import LanguageSwitcher from "@/components/language-switcher.svelte"
  import SignInLink from "@/components/sign-in-link.svelte"
  import TobyIcon from "@/components/toby-icon.svelte"
  import { landingReferral } from "@/lib/landing_referral.svelte"
  import { homePath } from "@/lib/locale-routes"
  import { goPath } from "@/routes"
  import { getInitials } from "@/runes/use-initials"
  import type { Auth } from "@/types"

  type Props = {
    signed_in?: boolean
    /** Trademark-mark the logo lockup — set ONLY by the home page
        (`/:lang` root); every other marketing page renders the plain
        icon. */
    tm?: boolean
  }
  const { signed_in = false, tm = false }: Props = $props()

  const homeHref = $derived(homePath($locale ?? "en"))

  // Arriving from a site's "Made with TOBY" badge as one of its members, the
  // pill opens that site's editor on the page they came from instead of /go.
  const shortcut = $derived(signed_in ? landingReferral.shortcut : null)

  // The avatar menu (identity + log out) needs the full shared `auth` prop,
  // not just the `signed_in` boolean. Marketing controllers inherit
  // InertiaController, so `auth` (user + session) and `site` are shared even
  // on the logged-out chrome whenever a session exists — so UserMenuContent
  // works here exactly as it does in the app header.
  const auth = $derived(page.props.auth as Auth | undefined)

  // The avatar menu is imported on demand (see marketing-user-menu.svelte):
  // its bits-ui menu stack must not ride the marketing page's preloaded
  // chunks, where a throttled phone pays for it before the hero paints. SSR
  // and the moment before the chunk lands show a static look-alike avatar.
  const avatarClass =
    "bg-muted text-muted-foreground inline-flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full text-xs font-medium"

  // The design's black nav pill ("Start free" / signed-in "Go to your site").
  const pillClass =
    "bg-ink dark:text-ink rounded-full px-4 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 max-[390px]:px-3 dark:bg-white"
</script>

<!-- The divider spans the full viewport (full-bleed border, like the section
     dividers below), but the content sits in the shared centered max-w-6xl
     column so the logo lines up with the hero, the sections, and the footer. -->
<header class="border-b border-black/[.06] dark:border-white/10">
  <div
    class="mx-auto flex max-w-6xl items-center justify-between px-6 py-3.5 lg:py-[18px]"
  >
    <a href={homeHref} use:inertia class="flex items-center gap-2.5">
      <TobyIcon size={36} {tm} />
      <span class="hidden text-[13.5px] font-semibold md:inline">
        {$_("brandSlogan")}
      </span>
    </a>
    <nav class="flex items-center gap-3.5 max-[390px]:gap-2 md:gap-[18px]">
      <!-- Widths measured across all 19 languages: the name fits signed out
           from 375px; signed in, the longer pill and the avatar need `sm`. -->
      <LanguageSwitcher
        plain
        labelClass={signed_in
          ? "hidden sm:inline"
          : "hidden min-[375px]:inline"}
      />
      {#if signed_in}
        <Link
          href={shortcut?.edit_url ?? goPath()}
          title={shortcut?.host}
          class={pillClass}
        >
          {$_("home.go_to_site")}
        </Link>
        {#if auth?.user}
          {#await import("@/components/marketing-user-menu.svelte")}
            <span class={avatarClass} aria-hidden="true">
              {#if auth.user.avatar}
                <img
                  src={auth.user.avatar}
                  alt=""
                  class="size-full object-cover"
                />
              {:else}
                {getInitials(auth.user.name)}
              {/if}
            </span>
          {:then { default: UserMenu }}
            <UserMenu {auth} />
          {/await}
        {/if}
      {:else}
        <SignInLink
          class="text-[13px] text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white"
        >
          {$_("home.sign_in")}
        </SignInLink>
        <SignInLink class={pillClass}>
          {$_("home.hero_cta")}
        </SignInLink>
      {/if}
    </nav>
  </div>
</header>
